import React, {useEffect, useState} from 'react';
import {BottomSheet, Carousel, Icon, Modal, Typography} from "@bonprix/design-system";
import ResponsiveImage from '@core/scripts/templates/ResponsiveImage.jsx';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import Slider from '@personalization/scripts/components/Slider.jsx';
import SliderLandmarkHelper from '@core/scripts/components/tracking/landmarks/slider-landmark-helper';
import PointOfSaleId from '@core/scripts/point-of-sale-id';
import RecommendationUtils from './recommendation-utils.js';
import Breakpoint from "@bonprix/pattern-library/components/breakpoint";
import {SideSectionAction} from "@product/sections/SideSectionAction.tsx";
import {SliderTypes} from "./slider-types.js";
import onceVisible from "@core/scripts/helper/once-visible";
import OutfitContent from "./OutfitContent.jsx";

const BREAKPOINTS = [
    { minWidth: 1600, items: 4 },
    { minWidth: 1280, items: 4 },
    { minWidth: 992, items: 4 },
    { minWidth: 768, items: 2 },
    { minWidth: 480, items: 2 },
    { minWidth: 0, items: 1 }
];
const SLIDE_CUT = 1/2;

export default function Outfits({
                                     itemNumber,
                                     shopId,
                                     texts,
                                     targetGroupLiving,
                                     outfits,
                                     componentRef,
                                     gap,
                                     fullWidth,
                                     showArrows
                                 }) {

    const [detailImage, setDetailImage] = useState(null);
    const [showDetailCloser, setShowDetailCloser] = useState(false);
    const newNumberOfItemsPerPage = RecommendationUtils.getNumberOfItemsPerPage(BREAKPOINTS);
    const [numberOfItemsPerPage, setNumberOfItemsPerPage] = useState(newNumberOfItemsPerPage);
    const [carouselPadding, setCarouselPadding] = useState(RecommendationUtils.calculateCarouselPadding(newNumberOfItemsPerPage, SLIDE_CUT));

    useEffect(() => {
        if (outfits && componentRef?.current) {
            onceVisible(
                componentRef.current,
                () => {
                    onOutfitsVisible(outfits, 0, numberOfItemsPerPage);
                },
                1
            );
        }

        const handleResize = () => {
            const newNumberOfItemsPerPage = RecommendationUtils.getNumberOfItemsPerPage(BREAKPOINTS);
            setNumberOfItemsPerPage(newNumberOfItemsPerPage);
            setCarouselPadding(RecommendationUtils.calculateCarouselPadding(newNumberOfItemsPerPage, SLIDE_CUT));
        };

        const subscriptions = [
            MessageBroker.subscribe(Messages.WINDOW.RESIZED, handleResize),
            MessageBroker.subscribe(SideSectionAction.CLOSED, resetDetailImage)
        ];

        return () => {
            subscriptions.forEach(subscription => MessageBroker.unsubscribe(subscription));
        };
    }, []);

    const onOutfitsVisible = (outfits, startIndex, endIndex) => {
        outfits.slice(startIndex, endIndex).forEach((outfit) => {
            const itemNumbers = outfit.outfitItems.map((item, index) => `${index + 1}:${item.itemNumber}`).join('\\');
            SliderLandmarkHelper.createAndSendOutfitVisibilityLandmark(SliderTypes.OUTFITS, outfit, itemNumbers, landmarkData);
        });
    };

    const landmarkData = {
        pageType: 'adp',
        type: 'article_to_article',
        pointOfSaleId: PointOfSaleId.OUTFITS,
        shopId,
        originItemNumber: itemNumber,
    };
    const onSlideChanged = (event) => {
        SliderLandmarkHelper.createSendSlideChangedLandmark(landmarkData);
        onOutfitsVisible(event.items, event.startIndex, event.startIndex + event.displayedItemCount);
    };
    const onCarouselSlideChanged = (index) => {
        SliderLandmarkHelper.createSendSlideChangedLandmark(landmarkData);
        onOutfitsVisible(outfits, index, index + numberOfItemsPerPage);
    };

    const onItemClicked = (event, clickedItem) => {
        event.preventDefault();
        SliderLandmarkHelper.createAndSendOutfitItemClickedLandmark(SliderTypes.OUTFITS, clickedItem, landmarkData);
        RecommendationUtils.navigateToTargetWithRecommendationTypeOrigin('outfits', clickedItem.path);
    };

    const onOutfitImageClicked = (outfit, event) => {
        event?.preventDefault();
        setDetailImage(outfit.detailImage);

        if (fullWidth) {
            const detailsComponent = (
                <>
                    {Breakpoint.keyIsIn(['start', 'small', 'medium']) &&
                        <div className="outfits--full-width__detail-image-wrapper">
                            <ResponsiveImage image={outfit.detailImage} className="outfits--full-width__detail-image" />
                        </div>
                    }
                    <OutfitContent outfit={outfit} landmarkData={landmarkData}/>
                </>
            );

            MessageBroker.publish(SideSectionAction.OPEN, {
                nodes: {
                    title: !Breakpoint.keyIsIn(['start', 'small']) ? texts['outfit_headline'] : null,
                    content: detailsComponent
                },
                snapPoints: [window.innerHeight * 0.9, 0]
            });
        }
    };

    const onClose = () => {
        resetDetailImage();
        MessageBroker.publish(SideSectionAction.CLOSE);
    };

    const resetDetailImage = () => {
        setDetailImage(null);
        setShowDetailCloser(false);
    };

    const onIsVisible = (event) => onOutfitsVisible(event.items, 0, event.displayedItemCount);

    const carousel = () => {
        if (fullWidth) {
            const callToActionText = targetGroupLiving ? texts.pdp_go_to_living_outfit : texts.pdp_go_to_outfit;
            return (
                <>
                    <div className="mb-4 ml-2.5 xl:ml-12">
                        <Typography variant="bonprix-h4" component="div" align="left">
                            {title}
                        </Typography>
                        <Typography variant="bonprix-subtitle1" color="on-bg-low" component="div" align="left">
                            {texts.pdp_product_recommendations_moreCombinations}
                        </Typography>
                    </div>
                    <Carousel label="label"
                              gap={gap}
                              arrows={showArrows}
                              pagination={false}
                              padding={{
                                  right: `${carouselPadding}px`,
                              }}
                              perPage={numberOfItemsPerPage}
                              perMove={numberOfItemsPerPage}
                              onMove={onCarouselSlideChanged}
                    >
                        {outfits.map((outfit, index) => (
                            <Carousel.Slide key={'outfits-carousel-' + index}>
                                <a href={window.location}
                                   className="outfits--full-width__outfit"
                                   onClick={(event) => onOutfitImageClicked(outfit, event)}>
                                    <div className="outfits--full-width__outfit-inner">
                                        <div className="outfits--full-width__outfit-image">
                                            <ResponsiveImage image={outfit.image}/>
                                        </div>
                                        <div className="outfits--full-width__button-wrapper">
                                            <Typography variant="bonprix-h6">
                                                {callToActionText}
                                            </Typography>
                                        </div>
                                        <div className="outfits--full-width__text-wrapper">
                                            <Typography variant="body2" color="on-bg-low">
                                                ({outfit.recommendationProductCardItems?.length} {texts.ada_artikel})
                                            </Typography>
                                        </div>
                                    </div>
                                </a>
                            </Carousel.Slide>
                        ))}
                    </Carousel>
                </>
            )
        }

        return (
            <Slider
                id="outfits"
                className="outfits"
                title={title}
                items={outfits}
                onIsVisible={onIsVisible}
                onSlideChanged={onSlideChanged}
                autoButtonPositioning
                sendVisibleLandmarkOnItemsChange
            >
                {outfits.map((outfit, index) => {
                    return (
                        <div className="outfits__slide" key={`outfit-${index}`}>
                            <div className="outfits__outfit-image"
                                 onClick={() => onOutfitImageClicked(outfit)}>
                                <ResponsiveImage image={outfit.image}/>
                            </div>
                            <div className="outfit__items">
                                {outfit.outfitItems.map((outfitItem) => {
                                    return (
                                        <a
                                            href={outfitItem.path}
                                            className="outfit__item"
                                            onClick={() => onItemClicked(event, outfitItem)}
                                            key={outfitItem.itemId}
                                        >
                                            <ResponsiveImage image={outfitItem.image}/>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </Slider>
        )
    };

    const modalContent = () => {
        return (
            <div className={fullWidth ? 'outfits--full-width__detail' : 'outfits__detail'}>
                {detailImage !== null && <>
                    <ResponsiveImage
                        className={fullWidth ? 'outfits--full-width__detail-image' : 'outfits__detail-image'}
                        image={detailImage}
                        onLoad={() => setShowDetailCloser(true)}/>
                    {showDetailCloser && !fullWidth &&
                        <span className={fullWidth ? 'outfits--full-width__detail-closer' : 'outfits__detail-closer'}>
                                <Icon variant='closer'/>
                            </span>}
                </>}
            </div>
        );
    }

    const title = targetGroupLiving ? texts.living_looks_headline : texts.outfits_headline;
    const modalBreakpoints = fullWidth ? ['large', 'xlarge', 'max'] : ['medium', 'large', 'xlarge', 'max'];

    return (
        <>
            <div className={`outfits${fullWidth ? '--full-width product-recommendations-with-spacing' : ''}`} ref={componentRef}>
                {carousel()}
            </div>

            {!fullWidth && Breakpoint.keyIsIn(['start', 'small']) && <BottomSheet
                isOpen={detailImage !== null}
                onClose={onClose}
                onSnap={onClose}
            >
                {detailImage !== null && <ResponsiveImage image={detailImage}/>}
            </BottomSheet>}

            {Breakpoint.keyIsIn(modalBreakpoints) && <Modal
                isOpen={detailImage !== null}
                zIndex={1000}
                onClose={onClose}
            >
                {fullWidth && <div className="outfits--full-width__detail-wrapper">
                    {modalContent()}
                </div>}
                {!fullWidth && modalContent()}
            </Modal>}
        </>
    );
}
